import { checklistItemsForResults } from '@/db/checklist'
import type { InspectionPriority, InspectionResult, InspectionResultStatus } from '@/db/types'

export interface InspectionResultSummary {
  good: number
  attention: number
  critical: number
  unanswered: number
  answered: number
  used: number
  total: number
  progressPercent: number
  priority: InspectionPriority
  provisional: boolean
}

export type InspectionSystemAggregate = 'good' | 'attention' | 'critical' | 'pending'

export interface InspectionSystemSummary {
  systemId: string
  systemLabel: string
  systemOrder: number
  total: number
  answered: number
  used: number
  unanswered: number
  good: number
  attention: number
  critical: number
  aggregate: InspectionSystemAggregate
}

export function calculateInspectionPriority(
  statuses: readonly InspectionResultStatus[],
): InspectionPriority {
  if (statuses.includes('critical')) return 'urgent_stop_immediately'
  if (statuses.includes('attention')) return 'schedule_within_15_days'
  if (statuses.includes('good')) return 'monitor'
  return 'pending'
}

function validateResults(
  results: readonly (Pick<InspectionResult, 'checklistItemId' | 'status'> &
    Partial<Pick<InspectionResult, 'id' | 'checklistItemLabel' | 'observation'>>)[],
) {
  const checklist = checklistItemsForResults(
    results.map((result) => ({
      checklistItemId: result.checklistItemId,
      checklistItemLabel: result.checklistItemLabel ?? '',
    })),
  )
  const expectedIds = new Set(checklist.map((item) => item.id))
  const resultsByItem = new Map<
    string,
    Pick<InspectionResult, 'checklistItemId' | 'status'> &
      Partial<Pick<InspectionResult, 'id' | 'checklistItemLabel' | 'observation'>>
  >()

  for (const result of results) {
    if (!expectedIds.has(result.checklistItemId)) {
      throw new Error(`Item de checklist desconhecido: ${result.checklistItemId}.`)
    }
    if (resultsByItem.has(result.checklistItemId)) {
      throw new Error(`Item de checklist duplicado: ${result.checklistItemId}.`)
    }
    resultsByItem.set(result.checklistItemId, result)
  }

  return { checklist, resultsByItem }
}

export function isInspectionResultUsed(
  result: Pick<InspectionResult, 'status'> & Partial<Pick<InspectionResult, 'id' | 'observation'>>,
  resultIdsWithPhotos: ReadonlySet<string> = new Set(),
): boolean {
  return (
    result.status !== 'unanswered' ||
    Boolean(result.observation?.trim()) ||
    Boolean(result.id && resultIdsWithPhotos.has(result.id))
  )
}

export function summarizeInspectionResults(
  results: readonly (Pick<InspectionResult, 'checklistItemId' | 'status'> &
    Partial<Pick<InspectionResult, 'id' | 'checklistItemLabel' | 'observation'>>)[],
  resultIdsWithPhotos: ReadonlySet<string> = new Set(),
): InspectionResultSummary {
  const { checklist, resultsByItem } = validateResults(results)
  const statuses = checklist.map(
    (item) => resultsByItem.get(item.id)?.status ?? ('unanswered' as const),
  )
  const good = statuses.filter((status) => status === 'good').length
  const attention = statuses.filter((status) => status === 'attention').length
  const critical = statuses.filter((status) => status === 'critical').length
  const unanswered = statuses.filter((status) => status === 'unanswered').length
  const answered = good + attention + critical
  const used = [...resultsByItem.values()].filter((result) =>
    isInspectionResultUsed(result, resultIdsWithPhotos),
  ).length

  return {
    good,
    attention,
    critical,
    unanswered,
    answered,
    used,
    total: checklist.length,
    progressPercent: (answered / checklist.length) * 100,
    priority: calculateInspectionPriority(statuses),
    provisional: unanswered > 0,
  }
}

export function summarizeInspectionSystems(
  results: readonly (Pick<InspectionResult, 'checklistItemId' | 'status'> &
    Partial<Pick<InspectionResult, 'id' | 'checklistItemLabel' | 'observation'>>)[],
  resultIdsWithPhotos: ReadonlySet<string> = new Set(),
): InspectionSystemSummary[] {
  const { checklist, resultsByItem } = validateResults(results)
  const systemIds = [...new Set(checklist.map((item) => item.systemId))]

  return systemIds.map((systemId) => {
    const items = checklist.filter((item) => item.systemId === systemId)
    const first = items[0]
    if (!first) throw new Error(`Sistema oficial desconhecido: ${systemId}.`)
    const systemResults = items
      .map((item) => resultsByItem.get(item.id))
      .filter((result) => result !== undefined)
    const statuses = items.map(
      (item) => resultsByItem.get(item.id)?.status ?? ('unanswered' as const),
    )
    const good = statuses.filter((status) => status === 'good').length
    const attention = statuses.filter((status) => status === 'attention').length
    const critical = statuses.filter((status) => status === 'critical').length
    const unanswered = statuses.filter((status) => status === 'unanswered').length
    const used = systemResults.filter((result) =>
      isInspectionResultUsed(result, resultIdsWithPhotos),
    ).length
    const aggregate: InspectionSystemAggregate =
      critical > 0 ? 'critical' : attention > 0 ? 'attention' : good > 0 ? 'good' : 'pending'

    return {
      systemId,
      systemLabel: first.systemLabel,
      systemOrder: first.systemOrder,
      total: items.length,
      answered: items.length - unanswered,
      used,
      unanswered,
      good,
      attention,
      critical,
      aggregate,
    }
  })
}

export function findFirstPendingResult<T extends Pick<InspectionResult, 'itemOrder' | 'status'>>(
  results: readonly T[],
): T | undefined {
  return [...results]
    .sort((left, right) => left.itemOrder - right.itemOrder)
    .find((result) => result.status === 'unanswered')
}
